// El botón flotante "Preguntále a Atlas" de la esquina inferior derecha —
// ADR-2026-09-10-el-boton-flotante-de-preguntale-a-atlas.
//
// Hasta ahora el panel lateral se abría sólo desde las pantallas que tenían un
// `BotonPreguntaleAtlas` en su flujo (el estudio de marketing, la ficha de un
// lanzamiento). El panel es parte del cascarón y vive en TODAS las pantallas logueadas;
// lo que faltaba era una forma de abrirlo desde cualquiera. Esto es esa forma.
//
// Es parte del CASCARÓN, hermano de `PanelChatAtlas`, y NO un `fixed` puesto desde una
// página: en esta app un flotante montado adentro de una página se mete debajo de la
// sidebar y del panel de contenido (`project_fixed_panel_stacking_trap`). Desde el
// cascarón, `fixed` es `fixed` de verdad.
//
// Reglas de aparición:
//   • Sólo si la empresa prendió la función avanzada `boton_flotante_chat` en
//     Configuración → Funciones avanzadas. APAGADA por defecto
//     (ADR-2026-09-10-el-flotante-del-chat-es-una-funcion-avanzada): el resto sigue
//     abriendo el chat desde los botones en flujo de cada pantalla y desde la sidebar.
//   • Sólo en desktop. En mobile el chat es a pantalla completa y la esquina inferior es
//     de la barra de navegación; ahí siguen los botones en flujo de cada pantalla.
//   • Se esconde mientras el panel está abierto (ocuparía justo la franja del panel) y en
//     la pantalla dedicada `/chat` (sería abrir lo que ya se está mirando).
//   • Se esconde en el lienzo de un diseño de indumentaria, que tiene el botón en su
//     encabezado (`elFlotanteNoVa`, ADR-2026-09-17-el-lienzo-del-diseno-no-lleva-flotante).
//   • Se esconde mientras hay CUALQUIER cosa abierta encima del contenido —un panel, un
//     menú, un sheet, el panel de un modo— porque si no se queda en la esquina tapándole
//     el botón al panel que el usuario acaba de abrir
//     (ADR-2026-09-13-el-flotante-se-va-si-hay-algo-abierto). Eso incluye el panel de los
//     modos, así que ahí el chat se abre desde la sidebar: decisión de Mati, con la regla
//     simple ganándole al caso fino.
//   • Comparte la esquina con el aviso de "generando de fondo" (`AvisoGenerandoDeFondo`,
//     `fixed bottom-6 right-6`). Cuando ese aviso está, el botón sube para no taparlo:
//     lee las mismas fuentes que el aviso, así los dos deciden con el mismo dato.
//
// z-[140]: debajo del aviso (`z-[150]`) y de los sheets (`z-[210]`), con los que comparte
// esquina. Ya NO es una decisión sobre el panel de los modos (`z-[100]`): desde
// ADR-2026-09-13-el-flotante-se-va-si-hay-algo-abierto el botón no convive con nada abierto
// —se esconde—, así que el z sólo ordena la esquina contra el aviso.

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { avisoGenerandoDeFondo } from '../../pages/v2/generacionesEnCurso';
import {
  useCompanyIdDelUsuario,
  useGeneracionesDeEmpresa,
  usePantallasDeCarga,
} from '../../pages/v2/useGeneracionesEnCurso';
import { useBotonFlotanteChat } from '../../hooks/useFuncionesAvanzadas';
import { useChatAtlasCascaron } from '../ChatAtlasContext';
import { elFlotanteNoVa, elFlotanteVaSiempre, esTiendaOnline, pantallaDeRuta } from './pantallaDeRuta';
import { CumbreMascot } from '../../components/CumbreMascot';
import { RUTA_CHAT_ATLAS } from './rutaChatAtlas';
import { useEsEscritorio } from './useEsEscritorio';
import { useHayAlgoAbiertoEncima } from './useHayAlgoAbiertoEncima';

export const BotonFlotanteChat: React.FC = () => {
  // El contexto CHICO: sólo necesita saber si está abierto y poder abrir. Leer el grande
  // lo haría re-renderizar con cada token del stream, en todas las pantallas.
  const { abierto, abrirChat } = useChatAtlasCascaron();
  // Cacheado en localStorage y escucha el interruptor de Configuración: prenderlo lo
  // muestra sin recargar.
  const habilitado = useBotonFlotanteChat();
  const esEscritorio = useEsEscritorio();
  // Cualquier cosa abierta encima del contenido: el botón se va (ver el encabezado).
  const algoAbierto = useHayAlgoAbiertoEncima();
  const location = useLocation();

  // ¿Está el aviso de "generando de fondo" en la esquina? Mismas fuentes que él.
  const companyId = useCompanyIdDelUsuario();
  const generaciones = useGeneracionesDeEmpresa(companyId);
  const pantallasDeCarga = usePantallasDeCarga();
  const hayAviso = useMemo(
    () => !!avisoGenerandoDeFondo(generaciones, pantallasDeCarga),
    [generaciones, pantallasDeCarga],
  );

  // El interruptor de Configuración decide en casi todas, pero no en las que no
  // tienen otra entrada al agente: ahí apagarlo sería esconder la función, no el
  // atajo (ADR-2026-09-15-en-un-lanzamiento-el-flotante-no-es-opcional).
  if (!habilitado && !elFlotanteVaSiempre(location.pathname)) return null;
  // En el canvas de Flujos el botón lo dibuja el propio canvas (`BotonPreguntaleAtlas`, en su misma
  // capa, así el visor y el panel del nodo quedan por encima); éste, que es `fixed` por encima de
  // todo, ahí no va (ADR-2026-09-16-flujos-el-flotante-es-la-entrada-al-chat).
  if (location.pathname.startsWith('/flujo/')) return null;
  if (!esEscritorio || abierto || algoAbierto) return null;
  if (location.pathname === RUTA_CHAT_ATLAS) return null;
  // Pantallas con su propio "Preguntále a Atlas" en el encabezado, donde la esquina
  // tapa el trabajo (ADR-2026-09-17-el-lienzo-del-diseno-no-lleva-flotante).
  if (elFlotanteNoVa(location.pathname)) return null;

  const pantalla = pantallaDeRuta(location.pathname);
  const abrir = () =>
    abrirChat({
      contexto: { pantalla, ruta: location.pathname },
      vista: 'panel',
    });

  // En Tienda online el flotante es redondo, con el degradé de Atlas y la mascota: ahí
  // el agente es el que sabe de la tienda y sus métricas, y Maxi pidió que se note como
  // "hablá con el agente" y no como un acceso más
  // (ADR-2026-10-06-el-flotante-de-la-tienda-lleva-la-mascota). En el resto de la app
  // sigue la pastilla blanca de siempre.
  if (esTiendaOnline(location.pathname)) {
    return (
      <button
        type="button"
        onClick={abrir}
        aria-label="Preguntále a Atlas sobre tu tienda"
        title="Preguntále a Atlas sobre tu tienda, tus ventas o tus lanzamientos"
        className="group fixed right-6 z-[140] grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-brand-primary via-[color:color-mix(in_srgb,var(--brand-primary)_80%,var(--brand-accent))] to-brand-accent text-white shadow-[0_12px_28px_rgb(var(--brand-primary-rgb)_/_0.35),0_2px_6px_rgb(var(--brand-black-rgb)_/_0.12)] ring-4 ring-white transition-[bottom,box-shadow,transform] duration-300 ease-out hover:shadow-[0_16px_36px_rgb(var(--brand-primary-rgb)_/_0.45),0_2px_6px_rgb(var(--brand-black-rgb)_/_0.12)] motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-brand-primary"
        style={{ bottom: hayAviso ? 88 : 24 }}
      >
        <CumbreMascot comoIcono decorativa size={28} className="h-auto w-7" />
        {/* El nombre aparece al pasar el mouse: el botón es un ícono, pero tiene que decir qué abre. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-full bg-brand-black py-1.5 px-3 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          Preguntále a Atlas
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={abrir}
      aria-label="Preguntále a Atlas"
      // Fondo BLANCO (pedido de Matías), no el azul de la acción principal: es un acceso
      // permanente en la esquina, no un CTA. Mismo lenguaje que el aviso de "generando de
      // fondo" con el que comparte esquina (blanco, sombra suave, borde finito) y que la
      // variante `sutil` de `BotonPreguntaleAtlas`, cuyas clases de borde reusa. El ícono
      // lleva el azul.
      // Padding 2:1 (py-2.5 / px-5), sin alto fijo. El desplazamiento cuando hay aviso
      // va en `style` porque el aviso mide ~46px y `bottom-[88px]` no es un token del
      // proyecto; la transición es la misma de las demás cosas del cascarón.
      className="fixed right-6 z-[140] inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-white py-2.5 px-5 text-[14px] font-semibold text-brand-black shadow-[0_12px_32px_rgb(var(--brand-black-rgb)_/_0.14),0_2px_6px_rgb(var(--brand-black-rgb)_/_0.08)] border border-[rgb(var(--brand-black-rgb)_/_0.1)] transition-[bottom,box-shadow,transform,color,border-color] duration-300 ease-out hover:text-brand-primary hover:border-[rgb(var(--brand-primary-rgb)_/_0.35)] hover:shadow-[0_14px_36px_rgb(var(--brand-black-rgb)_/_0.18),0_2px_6px_rgb(var(--brand-black-rgb)_/_0.08)] motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      style={{ bottom: hayAviso ? 88 : 24 }}
    >
      <Sparkles className="h-4 w-4 shrink-0 text-brand-primary" aria-hidden="true" />
      Preguntále a Atlas
    </button>
  );
};

export default BotonFlotanteChat;
